/**
 * useWorkspacePageLogic - Custom hook for workspace page business logic
 * Extracted from AppWorkspacePage to centralize all business logic
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceList } from '@/modules/app/hooks/useWorkspace';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { debugLog, debugError, isDebugMode } from '@/lib/debugUtils';

interface WorkspaceCounts {
  keywordCount: number;
  searchCount: number;
  aiAnalysisCount: number;
}

interface WorkspacePageState {
  searchFilter: string;
  selectedWorkspaces: Set<string>;
  hasTriedFetch: boolean;
  isDeleteDialogOpen: boolean;
  workspaceToDelete: any | null;
  confirmationText: string;
  isDeleting: boolean;
  workspaceCounts: Record<string, WorkspaceCounts>;
  isLoadingCounts: boolean;
}

export const useWorkspacePageLogic = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, userId, user } = useAuth();
  const { 
    workspaces, 
    fetchWorkspaces,
    refreshWorkspaces, 
    isLoading, 
    error, 
    getErrorMessage,
    clearError 
  } = useWorkspaceList();

  const [state, setState] = useState<WorkspacePageState>({
    searchFilter: '',
    selectedWorkspaces: new Set<string>(),
    hasTriedFetch: false,
    isDeleteDialogOpen: false,
    workspaceToDelete: null,
    confirmationText: '',
    isDeleting: false,
    workspaceCounts: {},
    isLoadingCounts: false,
  });

  // Fetch workspace counts from database
  const fetchWorkspaceCounts = useCallback(async () => {
    if (!userId || workspaces.length === 0) return;
    
    setState(prev => ({ ...prev, isLoadingCounts: true }));
    
    try {
      const counts: Record<string, WorkspaceCounts> = {};
      
      // Fetch counts for all workspaces in parallel
      await Promise.all(
        workspaces.map(async (workspace) => {
          try {
            // Get keyword count from keyword_consolidated_data
            const { count: keywordCount, error: keywordError } = await supabase
              .from('keyword_consolidated_data')
              .select('*', { count: 'exact', head: true })
              .eq('workspace_id', workspace.id)
              .eq('user_id', userId);

            // Get unique search count (count distinct primary_keyword)
            const { data: searchData, error: searchError } = await supabase
              .from('keyword_consolidated_data')
              .select('primary_keyword')
              .eq('workspace_id', workspace.id)
              .eq('user_id', userId);

            // Get AI analysis counts
            const [
              { count: categorizationCount, error: catError },
              { count: contentBriefsCount, error: contentError },
              { count: keywordClustersCount, error: clusterError }
            ] = await Promise.all([
              supabase
                .from('keyword_categorization')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', workspace.id)
                .eq('user_id', userId),
              supabase
                .from('content_briefs')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', workspace.id)
                .eq('user_id', userId),
              supabase
                .from('keyword_clusters')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', workspace.id)
                .eq('user_id', userId)
            ]);

            const searchCount = new Set(searchData?.map(item => item.primary_keyword) || []).size;
            const aiAnalysisCount = (categorizationCount || 0) + 
                                  (contentBriefsCount || 0) + 
                                  (keywordClustersCount || 0);

            counts[workspace.id] = {
              keywordCount: keywordCount || 0,
              searchCount,
              aiAnalysisCount
            };

            debugLog(user?.primaryEmailAddress?.emailAddress, `[useWorkspacePageLogic] Counts for ${workspace.name}:`, counts[workspace.id]);
          } catch (err) {
            console.error(`Error fetching counts for workspace ${workspace.id}:`, err);
            counts[workspace.id] = { keywordCount: 0, searchCount: 0, aiAnalysisCount: 0 };
          }
        })
      );
      
      setState(prev => ({ ...prev, workspaceCounts: counts }));
    } catch (err) {
      console.error('Error fetching workspace counts:', err);
    } finally {
      setState(prev => ({ ...prev, isLoadingCounts: false }));
    }
  }, [userId, workspaces]);

  // Fetch workspaces only after authentication is complete and we have a userId
  useEffect(() => {
    const loadWorkspaces = async () => {
      if (!authLoading && isAuthenticated && userId && !state.hasTriedFetch) {
        try {
          debugLog(user?.primaryEmailAddress?.emailAddress, '[useWorkspacePageLogic] Authentication complete, fetching workspaces for user:', userId);
          await fetchWorkspaces();
          setState(prev => ({ ...prev, hasTriedFetch: true }));
        } catch (err) {
          console.error('Failed to fetch workspaces:', err);
          setState(prev => ({ ...prev, hasTriedFetch: true }));
        }
      }
    };
    
    loadWorkspaces();
  }, [authLoading, isAuthenticated, userId, state.hasTriedFetch, fetchWorkspaces]);

  // Fetch counts after workspaces are loaded
  useEffect(() => {
    if (workspaces.length > 0 && userId && !isLoading) {
      fetchWorkspaceCounts();
    }
  }, [workspaces, userId, isLoading, fetchWorkspaceCounts]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    try {
      await refreshWorkspaces();
      if (workspaces.length > 0) {
        await fetchWorkspaceCounts();
      }
    } catch (err) {
      console.error('Failed to refresh workspaces:', err);
    }
  }, [refreshWorkspaces, workspaces.length, fetchWorkspaceCounts]);

  // Filter workspaces based on search
  const filteredWorkspaces = workspaces.filter(workspace =>
    workspace.name.toLowerCase().includes(state.searchFilter.toLowerCase())
  );

  // Calculate stats from real workspace count data
  const totalSavedKeywords = workspaces.reduce((sum, ws) => sum + (state.workspaceCounts[ws.id]?.keywordCount || 0), 0);
  const totalSearches = workspaces.reduce((sum, ws) => sum + (state.workspaceCounts[ws.id]?.searchCount || 0), 0);
  const totalAiAnalyses = workspaces.reduce((sum, ws) => sum + (state.workspaceCounts[ws.id]?.aiAnalysisCount || 0), 0);

  // Handle workspace selection
  const handleWorkspaceSelect = useCallback((workspaceId: string, checked: boolean) => {
    setState(prev => {
      const newSelection = new Set(prev.selectedWorkspaces);
      if (checked) {
        newSelection.add(workspaceId);
      } else {
        newSelection.delete(workspaceId);
      }
      return { ...prev, selectedWorkspaces: newSelection };
    });
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setState(prev => ({
      ...prev,
      selectedWorkspaces: checked 
        ? new Set(filteredWorkspaces.map(ws => ws.id))
        : new Set()
    }));
  }, [filteredWorkspaces]);

  // Handle workspace navigation
  const handleViewWorkspace = useCallback((workspaceId: string) => {
    navigate(`/app/workspace/${workspaceId}`);
  }, [navigate]);

  // Handle workspace actions
  const handleWorkspaceUpdate = useCallback((workspaceId: string, newName: string) => {
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  const handleWorkspaceDelete = useCallback((workspaceId: string) => {
    setState(prev => {
      const newSelection = new Set(prev.selectedWorkspaces);
      newSelection.delete(workspaceId);
      return { ...prev, selectedWorkspaces: newSelection };
    });
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  const handleWorkspaceCreated = useCallback((newWorkspace: any) => {
    debugLog(user?.primaryEmailAddress?.emailAddress, '[useWorkspacePageLogic] New workspace created:', newWorkspace);
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  // Handle delete button click
  const handleDeleteClick = useCallback((workspace: any) => {
    setState(prev => ({
      ...prev,
      workspaceToDelete: workspace,
      isDeleteDialogOpen: true,
      confirmationText: ''
    }));
  }, []);

  // Handle the actual deletion
  const handleDeleteWorkspaceConfirm = useCallback(async () => {
    if (!state.workspaceToDelete) return;

    if (state.workspaceToDelete.is_default) {
      toast.error('Cannot delete the default workspace');
      return;
    }

    if (state.confirmationText.toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm');
      return;
    }

    setState(prev => ({ ...prev, isDeleting: true }));

    try {
      // Get chetan.ppc@gmail.com user ID from user_mapping table
      const { data: userMapping, error: userError } = await supabase
        .from('user_mapping')
        .select('supabase_id')
        .eq('email', 'chetan.ppc@gmail.com')
        .single();

      if (userError || !userMapping) {
        throw new Error('Could not find transfer user');
      }

      const transferUserId = userMapping.supabase_id;

      // Transfer workspace ownership to chetan.ppc@gmail.com instead of deleting
      const { error } = await supabase
        .from('workspaces')
        .update({ 
          user_id: transferUserId,
          updated_at: new Date().toISOString()
        })
        .eq('id', state.workspaceToDelete.id)
        .eq('user_id', state.workspaceToDelete.user_id);

      if (error) throw error;

      // Call delete handler to remove from UI
      handleWorkspaceDelete(state.workspaceToDelete.id);
      setState(prev => ({
        ...prev,
        isDeleteDialogOpen: false,
        confirmationText: '',
        workspaceToDelete: null
      }));
      
      toast.success('Workspace removed from your account');
    } catch (error: any) {
      console.error('Error transferring workspace:', error);
      toast.error(error.message || 'Failed to remove workspace');
    } finally {
      setState(prev => ({ ...prev, isDeleting: false }));
    }
  }, [state.workspaceToDelete, state.confirmationText, handleWorkspaceDelete]);

  // Handle dialog cancel
  const handleDeleteCancel = useCallback(() => {
    setState(prev => ({
      ...prev,
      isDeleteDialogOpen: false,
      confirmationText: '',
      workspaceToDelete: null
    }));
  }, []);

  // Handle search filter change
  const handleSearchFilterChange = useCallback((value: string) => {
    setState(prev => ({ ...prev, searchFilter: value }));
  }, []);

  // Handle confirmation text change
  const handleConfirmationTextChange = useCallback((text: string) => {
    setState(prev => ({ ...prev, confirmationText: text }));
  }, []);

  // Find default workspace or use first workspace
  const defaultWorkspace = workspaces.find(ws => ws.is_default) || workspaces[0];

  return {
    // State
    ...state,
    filteredWorkspaces,
    totalSavedKeywords,
    totalSearches,
    totalAiAnalyses,
    defaultWorkspace,
    
    // From workspace list hook
    workspaces,
    isLoading,
    error,
    getErrorMessage,
    clearError,
    
    // From auth
    authLoading,
    isAuthenticated,
    userId,
    user,
    
    // Handlers
    handleRefresh,
    handleWorkspaceSelect,
    handleSelectAll,
    handleViewWorkspace,
    handleWorkspaceUpdate,
    handleWorkspaceDelete,
    handleWorkspaceCreated,
    handleDeleteClick,
    handleDeleteWorkspaceConfirm,
    handleDeleteCancel,
    handleSearchFilterChange,
    handleConfirmationTextChange,
  };
};